import { Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';

import { DelegaciaAbrangenciaApiResponse, DelegaciaAbrangenciaSalvarApiRequest, DelegaciaResumoApiResponse } from '@/core/api/sicom-api/registro/orgao/delegacia.model';
import { OrgaoService } from '@/core/api/sicom-api/registro/orgao/orgao.service';
import { CustomService } from '@/shared/services/custom.service';
import { OrgaoApiResponse } from '@/core/api/sicom-api/registro/orgao/orgao.model';

export interface DelegaciaLinha {
    id: number;
    uf: string;
    delegacia: string;
    sigla: string;
    cnpj: string;
    situacao: string;
    dataAtualizacao: string;
    usuario: string;
}

export interface DelegaciaAbrangenciaLinha {
    idMunicipio: number;
    municipio: string;
    ufMunicipio: string;
}

export interface DelegaciaVisualizacao {
    dadosGerais: DelegaciaLinha;
    abrangencias: DelegaciaAbrangenciaLinha[];
}

export interface DelegaciaSalvarRequest {
    id?: number;
    uf: string;
    descricao: string;
    sigla: string;
    registro: string;
    situacao: 'Ativo' | 'Inativo';
    razaoSocial?: string;
    presidente?: string;
    horarioAtendimento?: string;
    enderecoGeografico?: string;
}

@Injectable({
    providedIn: 'root'
})
export class DelegaciasService extends CustomService {
    constructor(private orgaoService: OrgaoService) {
        super();
    }

    listarPorUf(uf: string): Observable<DelegaciaLinha[]> {
        return this.orgaoService.listarDelegaciasPorUf(uf).pipe(
            map((response) => response.map((item) => this.mapearResumo(item))),
            catchError(this.handleError)
        );
    }

    visualizar(idDelegacia: number, uf: string): Observable<DelegaciaVisualizacao> {
        return this.orgaoService.visualizarDelegacia(idDelegacia, uf).pipe(
            map((response) => ({
                dadosGerais: this.mapearResumo(response.dadosGerais),
                abrangencias: (response.abrangencias || []).map((item) => this.mapearAbrangencia(item))
            })),
            catchError(this.handleError)
        );
    }

    salvarDelegacia(request: DelegaciaSalvarRequest): Observable<DelegaciaLinha> {
        const payload = this.mapearDelegaciaParaPayload(request);
        const requisicao = request.id ? this.orgaoService.alterarDelegacia(payload) : this.orgaoService.cadastrarDelegacia(payload);

        return requisicao.pipe(
            map((response) => this.mapearOrgaoSalvo(response)),
            catchError(this.handleError)
        );
    }

    excluirDelegacia(id: number, uf: string): Observable<boolean> {
        return this.orgaoService.excluirDelegacia({ id, uf }).pipe(catchError(this.handleError));
    }

    listarAbrangencias(idDelegacia: number, uf: string): Observable<DelegaciaAbrangenciaLinha[]> {
        return this.orgaoService.listarAbrangenciasDelegacia(idDelegacia, uf).pipe(
            map((response) => (response || []).map((item) => this.mapearAbrangencia(item))),
            catchError(this.handleError)
        );
    }

    salvarAbrangencia(request: DelegaciaAbrangenciaSalvarApiRequest): Observable<DelegaciaAbrangenciaLinha> {
        return this.orgaoService.salvarAbrangenciaDelegacia(request).pipe(
            map((response) => this.mapearAbrangencia(response)),
            catchError(this.handleError)
        );
    }

    excluirAbrangencia(request: DelegaciaAbrangenciaSalvarApiRequest): Observable<boolean> {
        return this.orgaoService.excluirAbrangenciaDelegacia(request).pipe(catchError(this.handleError));
    }

    private mapearResumo(item: DelegaciaResumoApiResponse): DelegaciaLinha {
        const itemNormalizado = item as DelegaciaResumoApiResponse & {
            'dataAtualizacao:'?: string | null;
            'usuario:'?: string | null;
        };

        return {
            id: Number(item.id),
            uf: item.uf || '-',
            delegacia: item.delegacia || '-',
            sigla: item.sigla?.trim() || '-',
            cnpj: this.formatarCnpj(item.cnpj || ''),
            situacao: item.situacao || '-',
            dataAtualizacao: (itemNormalizado.dataAtualizacao ?? itemNormalizado['dataAtualizacao:'] ?? '').toString().trim(),
            usuario: (itemNormalizado.usuario ?? itemNormalizado['usuario:'] ?? '').toString().trim()
        };
    }

    private mapearAbrangencia(item: DelegaciaAbrangenciaApiResponse): DelegaciaAbrangenciaLinha {
        return {
            idMunicipio: Number(item.idMunicipio),
            municipio: item.municipio || '-',
            ufMunicipio: item.ufMunicipio || '-'
        };
    }

    private mapearDelegaciaParaPayload(request: DelegaciaSalvarRequest): OrgaoApiResponse {
        const registroLimpo = (request.registro || '').toString().replace(/\D/g, '');
        const descricao = (request.descricao || '').toString().trim();
        const razaoSocial = (request.razaoSocial || '').toString().trim() || descricao;

        return {
            id: request.id,
            idSolicitante: request.id,
            uf: (request.uf || '').toString().trim().toUpperCase(),
            razaoSocial,
            descricao,
            sigla: (request.sigla || '').toString().trim().toUpperCase(),
            registro: registroLimpo,
            presidente: (request.presidente || '').toString().trim(),
            situacao: request.situacao,
            dataAtualizacao: '',
            usuario: ''
        };
    }

    private mapearOrgaoSalvo(item: OrgaoApiResponse): DelegaciaLinha {
        return {
            id: Number(item.id || item.idSolicitante || 0),
            uf: item.uf || '-',
            delegacia: item.descricao || '-',
            sigla: item.sigla?.trim() || '-',
            cnpj: this.formatarCnpj(item.registro || ''),
            situacao: item.situacao || '-',
            dataAtualizacao: (item.dataAtualizacao || item['dataAtualizacao:'] || '').toString().trim(),
            usuario: (item.usuario || item['usuario:'] || '').toString().trim()
        };
    }

    private formatarCnpj(valor: string): string {
        const digitos = (valor || '').toString().replace(/\D/g, '');
        if (digitos.length !== 14) {
            return digitos || '-';
        }
        return `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}.${digitos.slice(5, 8)}/${digitos.slice(8, 12)}-${digitos.slice(12)}`;
    }
}
